import { motion } from "framer-motion";
import { Layers, LineChart, Zap, Clock, Heart } from "lucide-react";

const VALUES = [
  {
    icon: Layers,
    title: "Simplicidad Centralizada",
    desc: "Eliminamos la necesidad de coordinar múltiples proveedores resolviendo desde la campaña publicitaria y el diseño, hasta la logística de eventos y el soporte corporativo bajo un mismo techo.",
    span: "lg:col-span-7",
    bg: "https://customer-assets.emergentagent.com/job_dynamic-trust/artifacts/b0yxt7ww_simplicidad-centralizada.webp",
  },
  {
    icon: LineChart,
    title: "Traducción Digital Inteligente",
    desc: "En un mercado saturado de métricas complejas, transformamos los datos y algoritmos de los entornos digitales en estrategias claras, entendibles y orientadas a resultados económicos reales.",
    span: "lg:col-span-5",
    bg: "https://customer-assets.emergentagent.com/job_dynamic-trust/artifacts/55k32jcc_traduccion-digital.webp",
  },
  {
    icon: Zap,
    title: "Versatilidad Táctica y Operativa",
    desc: "Poseemos una estructura única que nos permite conectar la creatividad de una agencia de anuncios con la precisión de una empresa de servicios de apoyo administrativo y logística.",
    span: "lg:col-span-5",
    bg: "https://customer-assets.emergentagent.com/job_dynamic-trust/artifacts/zud7ii6e_versatilidad-tactica.webp",
  },
  {
    icon: Clock,
    title: "Optimización Absoluta del Tiempo",
    desc: "Al encargarnos de toda la cadena de promoción, producción impresa y empaque, liberamos a los directivos de la carga operativa para que se enfoquen en el núcleo de su negocio.",
    span: "lg:col-span-4",
    bg: "https://customer-assets.emergentagent.com/job_dynamic-trust/artifacts/1qrwxajl_optimizacion-tiempo.webp",
  },
  {
    icon: Heart,
    title: "Estrategias con Enfoque Humano",
    desc: "Diseñamos relaciones públicas y campañas que conectan emocionalmente en redes sociales, asegurando que tu marca no solo sea vista, sino recordada y elegida en el entorno digital.",
    span: "lg:col-span-3",
    bg: "https://customer-assets.emergentagent.com/job_dynamic-trust/artifacts/5962oovp_estrategias-enfoque-humano.webp",
  },
];

export const Values = () => {
  return (
    <section
      id="valores"
      data-testid="values-section"
      className="bg-[#F9F9F9] py-24 sm:py-32 border-t border-[#BFBFBF]/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#BF1828] font-medium">
              Distintivos de Nuestra Empresa
            </div>
            <h2
              data-testid="values-heading"
              className="mt-5 font-display font-medium text-4xl sm:text-5xl tracking-tight leading-[1.05] text-black max-w-xl"
            >
              Los principios que
              <br />
              <span className="text-[#BF1828]">dan forma</span> a cada proyecto.
            </h2>
          </div>
          <p className="font-body text-base text-[#595959] max-w-md">
            Una agencia se define por lo que entrega y por la forma en que lo
            entrega. Estos son los valores que nos sostienen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.article
                key={v.title}
                data-testid={`value-card-${i + 1}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`group relative overflow-hidden bg-white border border-[#BFBFBF]/60 p-8 hover:border-[#BF1828] hover:-translate-y-1 transition-all duration-300 ${v.span}`}
              >
                {/* Background image */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-cover bg-center opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500"
                  style={{ backgroundImage: `url(${v.bg})` }}
                />
                {/* White overlay for legibility */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/85 to-white/70 group-hover:from-white/90 group-hover:to-white/60 transition-all duration-500"
                />
                <div className="relative">
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 border border-black bg-white flex items-center justify-center group-hover:bg-[#BF1828] group-hover:border-[#BF1828] transition-colors duration-300">
                    <Icon
                      size={22}
                      strokeWidth={1.6}
                      className="text-black group-hover:text-white transition-colors duration-300"
                    />
                  </div>
                  <span className="font-display font-bold text-sm text-[#BFBFBF]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-8 font-display font-semibold text-xl sm:text-2xl leading-tight text-black tracking-tight">
                  {v.title}
                </h3>
                <p className="mt-4 font-body text-[15px] leading-relaxed text-[#595959]">
                  {v.desc}
                </p>
                <div className="mt-6 h-px bg-[#BFBFBF]/50 w-0 group-hover:w-full transition-all duration-500" />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
